import { Injectable } from '@angular/core';
import { LoggingService } from '../logging/logic/logging.service';
import { TranslateService } from '@ngx-translate/core';
import { Peripheral, PeripheralType } from 'src/app/_model/peripheral';
import { Capacitor } from '@capacitor/core';
import { IMSAction } from 'src/app/_model/menu';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';

export type PeripheralConfigStep = {
  image?: string;
  title: string;
  subtitle?: string;
  description: string;
  skip?: boolean;
  skipTo?: number;
  action?: () => void;
  actionTitle?: string;
};

@Injectable()
export class PeripheralConfigFactoryService {
  // Public Member

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
  ) {}

  public configStepsForPeripheral(
    peripheral: Peripheral,
  ): PeripheralConfigStep[] {
    switch (peripheral.type) {
      case PeripheralType.PT_SCANNER_NETUM:
        return this.netumConfigSteps();
      case PeripheralType.PT_PAYMENT_SIX:
        return this.sixConfigSteps(peripheral.isTestDevice);
      case PeripheralType.PT_CAMERA_THETA:
        return this.thetaConfigSteps();
      default:
        this.logger.log(
          this.constructor.name,
          'Unknown Peripheral Type',
          peripheral?.type,
        );
        return [];
    }
  }

  // Private Functions

  private sixConfigSteps(
    isTestDevice: boolean = false,
  ): PeripheralConfigStep[] {
    const platform = Capacitor.getPlatform();
    const configSteps: PeripheralConfigStep[] = [];

    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP1.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP1.DESCRIPTION',
      ),
      actionTitle: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP1.ACTION',
      ),
      action: () => {
        NativeSettings.open({
          optionAndroid: AndroidSettings.Bluetooth,
          optionIOS: IOSSettings.Bluetooth,
        });
      },
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP2.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP2.DESCRIPTION',
      ),
      actionTitle: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP2.ACTION',
      ),
      action: () => {
        NativeSettings.open({
          optionAndroid: AndroidSettings.Wireless,
          optionIOS: IOSSettings.Bluetooth,
        });
      },
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP3.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP3.DESCRIPTION',
      ),
      skip: true,
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP4.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP4.DESCRIPTION',
      ),
      skip: true,
      skipTo: 7,
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP5.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP5.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP5.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP6.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP6.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP6.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP7.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP7.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP7.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP8.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP8.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP8.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP9.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP9.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP9.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP10.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP10.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP10.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP11.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP11.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP11.DESCRIPTION',
      ),
    });
    // TODO: iOS has no BT Pan Option, probably Wifi needed
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP12.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP12.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP12.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP13.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP13.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP13.DESCRIPTION',
      ),
      actionTitle: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP13.ACTION',
      ),
      action: () => {
        NativeSettings.open({
          optionAndroid: AndroidSettings.Bluetooth,
          optionIOS: IOSSettings.Bluetooth,
        });
      },
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP14.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP14.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP14.DESCRIPTION',
      ),
      actionTitle: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP14.ACTION',
      ),
      action: () => {
        NativeSettings.open({
          optionAndroid: AndroidSettings.Bluetooth,
          optionIOS: IOSSettings.Bluetooth,
        });
      },
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP15.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP15.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP15.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP16.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP16.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP16.DESCRIPTION',
      ),
    });

    let description = isTestDevice
      ? this.translate.instant(
          'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP17.DESCRIPTION.TEST-DEVICE',
        )
      : this.translate.instant(
          'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP17.DESCRIPTION.PRODUCTIVE-DEVICE',
        );
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP17.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP17.SUBTITLE',
      description: description,
    });

    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP18.TITLE',
      ),
      subtitle: 'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP18.SUBTITLE',
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP18.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP19.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP19.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP20.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.SIX.CONFIG.STEP20.DESCRIPTION',
      ),
    });

    return configSteps;
  }

  private netumConfigSteps(): PeripheralConfigStep[] {
    const platform = Capacitor.getPlatform();
    const configSteps: PeripheralConfigStep[] = [];

    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP1.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP1.DESCRIPTION',
      ),
      image: 'assets/image/netum_reset_1.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP2.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP2.DESCRIPTION',
      ),
      image: 'assets/image/netum_reset_2.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP3.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP3.DESCRIPTION',
      ),
      image: 'assets/image/netum_reset_3.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP4.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP4.DESCRIPTION',
      ),
      image: 'assets/image/netum_reset_4.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP5.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP5.DESCRIPTION',
      ),
      image: 'assets/image/netum_bt_mode.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP6.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP6.DESCRIPTION',
      ),
      image:
        platform === 'ios'
          ? 'assets/image/netum_bt_ble.png'
          : 'assets/image/netum_bt_spp.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP7.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP7.DESCRIPTION',
      ),
      image: 'assets/image/netum_bt_unpair.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP8.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP8.DESCRIPTION',
      ),
      image: 'assets/image/netum_provide_code_id.png',
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP9.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.NETUM.CONFIG.STEP9.DESCRIPTION',
      ),
    });

    return configSteps;
  }

  private thetaConfigSteps(
    isTestDevice: boolean = false,
  ): PeripheralConfigStep[] {
    const platform = Capacitor.getPlatform();
    const configSteps: PeripheralConfigStep[] = [];

    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.THETA.CONFIG.STEP1.TITLE',
      ),
      description: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.THETA.CONFIG.STEP1.DESCRIPTION',
      ),
    });
    configSteps.push({
      title: this.translate.instant(
        'SERVICE.PERIPHERALS-CONFIG-FACTORY.THETA.CONFIG.STEP2.TITLE',
      ),
      description: this.translate.instant(platform === 'ios'
        ? 'SERVICE.PERIPHERALS-CONFIG-FACTORY.THETA.CONFIG.STEP2.DESCRIPTION.IOS'
        : 'SERVICE.PERIPHERALS-CONFIG-FACTORY.THETA.CONFIG.STEP2.DESCRIPTION.ANDROID',
      ),
    });
    
    return configSteps;
  }
}
